import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { getLocalizedName } from '../../lib/localization'
import { useContracts } from '../../hooks/useQueries'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { SECTION_ICONS } from '../../lib/section-icons'

interface UserContractsProps {
  org: string
  userUid: string
  userType?: string
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  closed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
}

export function UserContracts({ org, userUid, userType }: UserContractsProps) {
  const { t, i18n } = useTranslation('contracts')
  const locale = i18n.language?.split('-')[0] || 'en'
  const { data: contracts = [], isLoading } = useContracts(org, {
    userUid,
    include: ['stakeholders', 'assets', 'contract_type', 'stakeholder_type', 'user'],
    limit: 100,
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('list.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-16 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    )
  }

  if (contracts.length === 0) {
    if (userType === 'user') return null
    return (
      <Card data-testid="contact-contracts-card">
        <CardHeader>
          <CardTitle>{t('list.title_count', { count: 0 })}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState variant="inline" icon={SECTION_ICONS.contracts} title={t('list.none_for_contact')} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-testid="contact-contracts-card">
      <CardHeader>
        <CardTitle>{t('list.title_count', { count: contracts.length })}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-border">
          {contracts.map((contract: any) => {
            const stakeholder = contract.stakeholders?.find(
              (s: any) => s.user_uid === userUid
            )
            const roleName = getLocalizedName(stakeholder?.stakeholder_type?.names, locale)
            const contractType = getLocalizedName(contract.contract_type?.names, locale)
            const assets = contract.assets || []

            return (
              <div
                key={contract.uid}
                data-testid={`contract-row-${contract.uid}`}
                className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/$org/contracts/$contractUid"
                    params={{ org, contractUid: contract.uid }}
                    className="text-primary hover:text-primary/80 hover:underline font-medium text-sm"
                    data-testid={`contract-link-${contract.uid}`}
                  >
                    {contract.name}
                  </Link>

                  {contract.code && (
                    <span className="text-muted-foreground text-xs">{contract.code}</span>
                  )}

                  {contractType && (
                    <Badge variant="secondary">{contractType}</Badge>
                  )}

                  {roleName && (
                    <Badge variant="outline">{roleName}</Badge>
                  )}

                  {contract.status && (
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[contract.status] || ''}`}>
                      {t(`status.${contract.status}`, { defaultValue: t('status.unknown') })}
                    </span>
                  )}
                </div>

                {assets.length > 0 && (
                  <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                    {assets.map((ca: any) => {
                      const asset = ca.asset
                      if (!asset) return null
                      return (
                        <Link
                          key={ca.asset_uid}
                          to="/$org/assets/$assetUid"
                          params={{ org, assetUid: ca.asset_uid }}
                          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 hover:underline text-xs"
                          data-testid={`contract-asset-link-${ca.asset_uid}`}
                        >
                          <AppIcon tone="muted" path={SECTION_ICONS.assets} size="xs" />
                          {asset.name || ca.asset_uid}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
